import {
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SearchByTextSchemaType } from "@/Interface/Api/HomeInterface"; // Make sure this path is correct
import { cn } from "@/lib/utils";
import { AlertCircle, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import HighlightedText from "../HighlightText";

interface SearchDialogProps {
  data: SearchByTextSchemaType[] | undefined;
  isLoading: boolean;
  isError: boolean;
  hasSearched: boolean;
  searchText: string;
}

const SearchDialog = ({
  data,
  isLoading,
  isError,
  hasSearched,
  searchText,
}: SearchDialogProps) => {
  return (
    <DialogContent className="xl:max-w-7xl lg:max-w-5xl md:max-w-2xl w-full">
      <DialogHeader>
        <DialogTitle>Search Results</DialogTitle>
      </DialogHeader>

      <div className="w-full min-h-[200px] mt-4 relative">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center gap-2 text-muted-foreground bg-background/50">
            <Loader2 className="h-5 w-5 animate-spin" role="presentation"/>
            <span>Searching...</span>
          </div>
        )}

        {isError && (
          <div className="flex flex-col items-center justify-center gap-2 text-destructive">
            <AlertCircle className="h-8 w-8" />
            <p className="font-semibold">Error</p>
            <p className="text-center">
              Could not fetch search results. Please try again.
            </p>
          </div>
        )}

        {!isLoading && !isError && hasSearched && (
          <div>
            {data && data.length ? (
              <div className="space-y-4">
                <h3 className="text-sm font-normal">
                  Found {data.length} result(s)
                </h3>
                <div className="max-h-[40vh] overflow-y-auto pr-2">
                  {data.map((item, index) => (
                    <div key={index} className="border rounded-lg p-4 mb-3">
                      <a
                        href={item.route}
                        className="text-lg font-bold text-primary hover:underline"
                      >
                        {item.title}
                      </a>
                      <p className="text-muted-foreground mt-1 text-sm">
                        <HighlightedText
                          text={item.matchedText.slice(0, 200).concat("..")}
                          highlight={searchText}
                        />
                        <Link
                          to={item.route}
                          className="text-blue-600 hover:underline underline-offset-2"
                        >
                          see more
                        </Link>
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-center text-muted-foreground pt-8">
                No results found. Try a different search term.
              </p>
            )}
          </div>
        )}
      </div>
    </DialogContent>
  );
};

export default SearchDialog;
